import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { MapPin, Flame, TrendingUp, Users } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';

const log = createLogger('GYMSTREAKWIDGET');

interface GymStreak {
  current_streak: number;
  longest_streak: number;
  current_week_count: number;
  min_weekly_members: number;
  week_start_date: string;
}

export default function GymStreakWidget() {
  const router = useRouter();
  const { profile } = useAuth();
  const { theme } = useTheme();
  const [streak, setStreak] = useState<GymStreak | null>(null);
  const [gymName, setGymName] = useState<string>('');

  useEffect(() => {
    loadStreak();
  }, [profile]);

  const loadStreak = async () => {
    if (!profile || !profile.gym_id) return;

    try {
      const { data: streakData } = await supabase
        .from('gym_streaks')
        .select('*')
        .eq('gym_id', profile.gym_id)
        .maybeSingle();

      if (streakData) {
        setStreak(streakData);
      }

      const { data: gymData } = await supabase
        .from('gyms')
        .select('name')
        .eq('id', profile.gym_id)
        .maybeSingle();

      if (gymData) {
        setGymName(gymData.name);
      }
    } catch (error) {
      log.error('Error loading gym streak:', error);
    }
  };

  if (!streak || !profile?.gym_id) {
    return null;
  }

  const progress = (streak.current_week_count / streak.min_weekly_members) * 100;
  const isStreakActive = streak.current_week_count >= streak.min_weekly_members;

  return (
    <TouchableOpacity
      style={[styles.container, { backgroundColor: theme.colors.surface }]}
      onPress={() => router.push('/(tabs)/rank')}
    >
      <LinearGradient
        colors={['#8B5CF6', '#7C3AED']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.iconContainer}
      >
        <MapPin size={24} color={theme.colors.text} />
      </LinearGradient>

      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: theme.colors.text }]}>
            {gymName || 'Gym'} Streak
          </Text>
          {streak.current_streak > 0 && (
            <View style={styles.streakBadge}>
              <Flame size={14} color="#F59E0B" />
              <Text style={styles.streakText}>{streak.current_streak}</Text>
            </View>
          )}
        </View>

        <View style={styles.participationRow}>
          <Users size={14} color={theme.colors.textSecondary} />
          <Text style={[styles.participationText, { color: theme.colors.textSecondary }]}>
            {streak.current_week_count} / {streak.min_weekly_members} members this week
          </Text>
        </View>

        <View
          style={[styles.progressBar, { backgroundColor: theme.colors.surfaceVariant }]}
        >
          <View
            style={[
              styles.progressFill,
              {
                width: `${Math.min(progress, 100)}%`,
                backgroundColor: isStreakActive ? '#10B981' : '#F59E0B',
              },
            ]}
          />
        </View>

        {streak.longest_streak > 0 && (
          <View style={styles.stats}>
            <TrendingUp size={14} color={theme.colors.textTertiary} />
            <Text style={[styles.statsText, { color: theme.colors.textTertiary }]}>
              Best: {streak.longest_streak} weeks
            </Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 2,
    gap: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    gap: 6,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
  },
  streakBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#FEF3C7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 2,
  },
  streakText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#F59E0B',
  },
  participationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  participationText: {
    fontSize: 14,
  },
  progressBar: {
    height: 6,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  stats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statsText: {
    fontSize: 12,
  },
});
